import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type JSX,
  type PropsWithChildren
} from 'react';
import { storageKey, useAuth } from '@invite/app-core';
import { listUnits, switchTeam } from '../api/ownerApi';
import { ownerKeys } from '../api/queryKeys';
import type { OwnedUnit } from '../api/schemas';
import { isOwnerToken } from '../auth/ownerToken';
import { GateError, Pending } from '../auth/RequireOwner';
import { NoUnitsScreen } from '../auth/NoUnitsScreen';
import { initialUnit } from './initialUnit';

interface UnitContextValue {
  unit: OwnedUnit;
  units: OwnedUnit[];
  selectUnit: (id: number) => Promise<void>;
}

const UnitContext = createContext<UnitContextValue | null>(null);

const rememberId = (id: number): void => {
  try {
    localStorage.setItem(storageKey('unitId'), String(id));
  } catch {
    // Remembering the unit is a convenience.
  }
};

export const UnitProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const { principal, signIn } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ownerKeys.units, queryFn: listUnits, retry: false });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const teamId = principal?.teamId ?? null;
  const units = query.data;

  const unit = useMemo(
    () =>
      units?.find((u) => u.id === selectedId) ?? (units ? initialUnit(units, teamId) : undefined),
    [units, selectedId, teamId]
  );

  const selectUnit = useCallback(
    async (id: number): Promise<void> => {
      const target = units?.find((u) => u.id === id);
      if (target === undefined) {
        return;
      }
      const switching = target.teamId !== teamId;
      const login = switching ? await switchTeam(target.teamId) : null;
      if (login !== null && !isOwnerToken(login.token)) {
        throw new Error('switch-team returned a token without the owner role');
      }
      // Stored before signIn: the new token remounts the gate, and this provider with it.
      rememberId(id);
      setSelectedId(id);
      if (login !== null) {
        signIn(login.token, new Date(login.tokenExpiryDate));
      }
      queryClient.removeQueries({
        predicate: (q) =>
          q.queryKey[0] !== ownerKeys.units[0] || q.queryKey[1] !== ownerKeys.units[1]
      });
    },
    [units, teamId, signIn, queryClient]
  );

  const value = useMemo<UnitContextValue | null>(
    () => (unit && units ? { unit, units, selectUnit } : null),
    [unit, units, selectUnit]
  );

  if (query.isError) {
    return <GateError />;
  }
  if (units?.length === 0) {
    return <NoUnitsScreen />;
  }
  if (units !== undefined && unit === undefined) {
    // The gate switches to a listed team first, so this is a list that changed under us.
    return <GateError />;
  }
  if (value === null) {
    return <Pending />;
  }
  return <UnitContext.Provider value={value}>{children}</UnitContext.Provider>;
};

export const useUnit = (): UnitContextValue => {
  const value = useContext(UnitContext);
  if (value === null) {
    throw new Error('useUnit must be used inside UnitProvider');
  }
  return value;
};
